// Shared copy/download helpers for one-time MFA recovery codes, used by
// `recoveryCodeShare.js`'s `triggerRecoveryCodesDownload` — in turn shared by
// the RD-SEC-015 stepper's `MfaStepRecoveryCodes` (both the enrollment
// "codes" step and `MfaRegenerateCodesModal`'s codes screen) so every save
// destination stays byte-identical.

/** Plain newline-joined text — the clipboard + file payload. */
export function recoveryCodesText(codes) {
  return (codes || []).join('\n');
}

/**
 * Filesystem-safe slug for a filename segment (first name or email local-part).
 * Lowercase ASCII letters/digits only; everything else collapses to a single
 * hyphen. Empty input → empty string (caller falls back).
 */
export function slugifyFilenamePart(value) {
  return String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

/**
 * Prefer the first word of the full name; else the email local-part
 * (before @). Returns '' when neither yields a usable slug.
 */
export function recoveryCodesOwnerSlug({ fullName, email } = {}) {
  const first = String(fullName || '').trim().split(/\s+/)[0] || '';
  const fromName = slugifyFilenamePart(first);
  if (fromName) return fromName;
  const local = String(email || '').trim().split('@')[0] || '';
  return slugifyFilenamePart(local);
}

/** Local calendar date as YYYY-MM-DD (sorts cleanly in Downloads). */
export function recoveryCodesDateStamp(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * `{firstname}-{yyyy-mm-dd}-crewvex-recovery-codes.txt`
 * Falls back to `{yyyy-mm-dd}-crewvex-recovery-codes.txt` with no owner slug.
 */
export function buildRecoveryCodesFilename({ fullName, email, date } = {}) {
  const stamp = recoveryCodesDateStamp(date instanceof Date ? date : new Date());
  const owner = recoveryCodesOwnerSlug({ fullName, email });
  const base = owner
    ? `${owner}-${stamp}-crewvex-recovery-codes`
    : `${stamp}-crewvex-recovery-codes`;
  return `${base}.txt`;
}

/**
 * Download the codes as a .txt with a human header. Browser-only (creates an
 * object URL + anchor); no-op-safe to call from a click handler.
 *
 * @param {string[]} codes
 * @param {string} headerText – first line(s) of the file body
 * @param {object} [opts]
 * @param {string} [opts.fullName]
 * @param {string} [opts.email]
 * @param {string} [opts.filename] – override when tests need a fixed name
 */
export function downloadRecoveryCodes(codes, headerText, opts = {}) {
  const filename = opts.filename || buildRecoveryCodesFilename(opts);
  const blob = new Blob(
    [`${headerText}\n\n${recoveryCodesText(codes)}\n`],
    { type: 'text/plain;charset=utf-8' },
  );
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
